import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { LandingSettingsService } from "../../core/landing-settings.service";
import { landingMaterialImports } from "../../landing-material";

interface LandingLanguageOption {
  language: string;
  code: string;
  icon: string;
}

@Component({
  selector: "app-landing-page-toolbar",
  standalone: true,
  imports: [landingMaterialImports, TranslateModule, TablerIconsModule],
  templateUrl: "./landing-page-toolbar.component.html",
  styleUrls: ["./landing-page-toolbar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageToolbarComponent {
  private readonly settings = inject(LandingSettingsService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly theme = signal<"light" | "dark">("light");

  readonly languages: LandingLanguageOption[] = [
    {
      language: "English",
      code: "en",
      icon: "/assets/images/flag/icon-flag-en.svg",
    },
    {
      language: "Español",
      code: "es",
      icon: "/assets/images/flag/icon-flag-es.svg",
    },
  ];

  readonly selectedLanguage = signal<LandingLanguageOption>(this.languages[0]);

  constructor() {
    this.syncFromSettings();
    this.settings.notify
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncFromSettings());

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const language = this.languages.find((lang) => lang.code === event.lang);
        if (language) {
          this.selectedLanguage.set(language);
        }
      });
  }

  changeLanguage(language: LandingLanguageOption): void {
    this.translate.use(language.code);
    this.settings.setLanguage(language.code);
    this.selectedLanguage.set(language);
  }

  setTheme(theme: "light" | "dark"): void {
    this.settings.setOptions({ theme });
  }

  private syncFromSettings(): void {
    const options = this.settings.getOptions();
    this.theme.set(options.theme === "dark" ? "dark" : "light");

    const language =
      this.languages.find((lang) => lang.code === options.language) ??
      this.languages[0];
    this.selectedLanguage.set(language);
  }
}
